'use client';

import { useEffect } from 'react';
import { DevEvents } from '../@types';
import { BigNum } from '@velocity-exchange/sdk';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import { DriftWindow } from 'src/window/driftWindow';
import { useSpotMarketsLookup } from './spotMarkets/useSpotMarketsLookup';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { cleanupExpiredOverrides } from 'src/utils/dev/devLiquidationOverrides';
import useForceUpdate from './useForceUpdate';
import { useRenameSubaccount } from './driftActions/user/useRenameSubaccount';
import { useReclaimRent } from './driftActions/user/useReclaimRent';
import { useWithdrawCollateral } from './driftActions/spot-management/useWithdrawCollateral';
import { useDepositCollateral } from './driftActions/spot-management/useDepositCollateral';
import { useWithdrawDustPositions } from './driftActions/settlement/useWithdrawDustPositions';
import { useCancelVaultWithdrawal } from './driftActions/vaults/useCancelVaultWithdrawal';

const ALREADY_SETUP_REF = { current: false };

// # Type for event handlers for DevEvents interface .. [key, handler]
type EventHandlerConfig = [keyof DevEvents, DevEvents[keyof DevEvents]];

const useDevActions = () => {
	const spotMarketsLookup = useSpotMarketsLookup();
	const triggerRefresh = useForceUpdate();

	const getState = useDriftStore((s) => s.get);
	const getDriftClient = () => getState().driftClient.client;

	const { renameSubaccount } = useRenameSubaccount();
	const { reclaimRent } = useReclaimRent();
	const { withdrawCollateral } = useWithdrawCollateral();
	const { depositCollateralForExistingAccount } = useDepositCollateral();
	const { withdrawDustPositions } = useWithdrawDustPositions();
	const { cancelVaultWithdrawalRequest } = useCancelVaultWithdrawal();

	// # Logic for each event handler
	const handleWithdrawAllDust: DevEvents['withdraw_all_dust'] = () => {
		withdrawDustPositions();
	};

	const handleSettleAllValidOracleMarkets: DevEvents['settle_all_valid_oracle_markets'] =
		() => {};

	const handleWithdraw5Usdc: DevEvents['withdraw_5_usdc'] = () => {
		const USDCMarket = spotMarketsLookup[0];

		withdrawCollateral({
			amount: BigNum.fromPrint('5', USDCMarket.precisionExp),
			spotMarketConfig: USDCMarket,
		});
	};

	const createAnchorErrorTx: DevEvents['force_anchor_error_tx'] = () => {
		renameSubaccount(1_000, 'bler');
	};

	const createDriftErrorTx: DevEvents['force_drift_error_tx'] = () => {
		const driftClient = getDriftClient();
		const userAccount = driftClient?.getUserAccount(
			driftClient.activeSubAccountId
		);
		const vaultClient = useVaultsStore.getState().vaultClient;
		if (!userAccount || !vaultClient) return;

		cancelVaultWithdrawalRequest(DEFAULT_PUBLIC_KEY, vaultClient);
	};

	const createWalletbalanceError: DevEvents['force_insufficient_wallet_balance_error'] =
		() => {
			const spotMarket = spotMarketsLookup[0];

			depositCollateralForExistingAccount(
				BigNum.fromPrint((50_000_000).toString(), spotMarket.precisionExp),
				spotMarket
			);
		};

	const handleReclaimRent: DevEvents['reclaim_rent'] = () => {
		const driftClient = getDriftClient();
		if (!driftClient || driftClient.activeSubAccountId === undefined) return;

		reclaimRent(driftClient.activeSubAccountId);
	};

	// # Config for event handlers
	const eventHandlerConfigs: EventHandlerConfig[] = [
		['withdraw_all_dust', handleWithdrawAllDust],
		['settle_all_valid_oracle_markets', handleSettleAllValidOracleMarkets],
		['withdraw_5_usdc', handleWithdraw5Usdc],
		['force_anchor_error_tx', createAnchorErrorTx],
		['reclaim_rent', handleReclaimRent],
		['force_drift_error_tx', createDriftErrorTx],
		['force_insufficient_wallet_balance_error', createWalletbalanceError],
	];

	// # Connect event handlers to the event emitter
	useEffect(() => {
		if (ALREADY_SETUP_REF.current) return;

		ALREADY_SETUP_REF.current = true;

		const devEventEmitter = DriftWindow.devEventEmitter;

		eventHandlerConfigs.forEach(([key, handler]) => {
			devEventEmitter.on(key, handler);
		});

		return () => {
			eventHandlerConfigs.forEach(([key, handler]) => {
				devEventEmitter.off(key, handler);
			});
			ALREADY_SETUP_REF.current = false;
		};
	}, []);

	// Cleanup expired liquidation overrides periodically
	useEffect(() => {
		const interval = setInterval(() => {
			const removed = cleanupExpiredOverrides();
			if (removed) {
				triggerRefresh();
			}
		}, 1000); // Check every second

		return () => {
			clearInterval(interval);
		};
	}, [triggerRefresh]);
};

export default useDevActions;
